import { useState } from 'react';

const ProductWaitlistForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('interest', formData.interest);

      const response = await fetch(
        'https://readdy.ai/api/form/d6akdg5t1k61917cms4g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitMessage('You are on the list. We will notify you as soon as the collection launches.');
        setFormData({ name: '', email: '', interest: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitMessage('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section className="py-32 px-6 lg:px-12 bg-warmWhite">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <div className="w-10 h-[1px] bg-bronze/60"></div>
            <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.3em] uppercase">
              Early Access
            </span>
            <div className="w-10 h-[1px] bg-bronze/60"></div>
          </div>
          
          <h2 className="font-display text-4xl md:text-5xl font-light text-charcoal mb-6 italic">
            Join the Waitlist
          </h2>
          
          <p className="text-textGray text-sm font-sans font-light leading-relaxed max-w-2xl mx-auto mb-4">
            Be the first to access the Botanical Neuro-Modulation Collection when it launches.
          </p>
          
          <p className="text-charcoal/70 text-[13px] font-sans italic max-w-xl mx-auto">
            Waitlist members receive priority access, exclusive pricing, and detailed formulation insights.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-cream/50 rounded-2xl p-8 border border-warmGray/10 text-center">
            <div className="w-12 h-12 bg-bronze/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-vip-crown-line text-bronze text-lg" aria-hidden="true"></i>
            </div>
            <h3 className="font-sans text-sm font-semibold text-charcoal mb-2 tracking-wide">
              Priority Access
            </h3>
            <p className="text-textGray text-[12px] font-sans font-light leading-relaxed">
              First access before public launch
            </p>
          </div>

          <div className="bg-cream/50 rounded-2xl p-8 border border-warmGray/10 text-center">
            <div className="w-12 h-12 bg-bronze/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-price-tag-3-line text-bronze text-lg" aria-hidden="true"></i>
            </div>
            <h3 className="font-sans text-sm font-semibold text-charcoal mb-2 tracking-wide">
              Exclusive Pricing
            </h3>
            <p className="text-textGray text-[12px] font-sans font-light leading-relaxed">
              Special launch pricing for early supporters
            </p>
          </div>

          <div className="bg-cream/50 rounded-2xl p-8 border border-warmGray/10 text-center">
            <div className="w-12 h-12 bg-bronze/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-bronze text-lg" aria-hidden="true"></i>
            </div>
            <h3 className="font-sans text-sm font-semibold text-charcoal mb-2 tracking-wide">
              Formulation Insights
            </h3>
            <p className="text-textGray text-[12px] font-sans font-light leading-relaxed">
              Detailed ingredient rationale and usage protocols
            </p>
          </div>
        </div>

        {/* Form */}
        <form
          id="product-waitlist"
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl p-10 lg:p-14 shadow-soft border border-warmGray/10"
          data-readdy-form
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="pw-name" className="block text-[11px] font-sans font-semibold text-charcoal mb-2.5 tracking-wider uppercase">
                Full Name *
              </label>
              <input
                type="text"
                id="pw-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-5 py-3.5 border border-warmGray/30 rounded-xl focus:outline-none focus:border-bronze/50 transition-colors text-[13px] font-sans text-charcoal bg-warmWhite"
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="pw-email" className="block text-[11px] font-sans font-semibold text-charcoal mb-2.5 tracking-wider uppercase">
                Email Address *
              </label>
              <input
                type="email"
                id="pw-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-5 py-3.5 border border-warmGray/30 rounded-xl focus:outline-none focus:border-bronze/50 transition-colors text-[13px] font-sans text-charcoal bg-warmWhite"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="mb-9">
            <label htmlFor="pw-interest" className="block text-[11px] font-sans font-semibold text-charcoal mb-2.5 tracking-wider uppercase">
              Most Interested In
            </label>
            <select
              id="pw-interest"
              name="interest"
              value={formData.interest}
              onChange={handleChange}
              className="w-full px-5 py-3.5 border border-warmGray/30 rounded-xl focus:outline-none focus:border-bronze/50 transition-colors text-[13px] font-sans text-charcoal bg-warmWhite cursor-pointer"
            >
              <option value="">Select a product</option>
              <option value="Neural Calm Botanical Serum">Neural Calm Botanical Serum</option>
              <option value="Structural Renewal Elixir">Structural Renewal Elixir</option>
              <option value="Meridian Balance Mist">Meridian Balance Mist</option>
              <option value="Full Collection">Full Collection</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-bronze text-white rounded-full text-[13px] font-sans font-semibold hover:bg-softGold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap tracking-wide"
          >
            {isSubmitting ? 'Joining...' : 'Join Waitlist'}
          </button>

          {submitMessage && (
            <div className={`mt-7 p-5 rounded-xl ${
              submitMessage.includes('list') ? 'bg-cream text-charcoal' : 'bg-red-50 text-red-600'
            }`}>
              <p className="text-[12px] font-sans leading-relaxed">{submitMessage}</p>
            </div>
          )}
        </form>

        {/* Footer Note */}
        <p className="text-center text-textGray text-[11px] font-sans font-light mt-10 italic">
          Your information will only be used to notify you about the collection launch. No spam, ever.
        </p>
      </div>
    </section>
  );
};

export default ProductWaitlistForm;
